import React, {useCallback, useMemo} from 'react';
import {
  Box,
  Typography,
  Checkbox,
  Chip,
  IconButton,
  CircularProgress,
  Skeleton,
  Tooltip,
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import LanguageIcon from '@mui/icons-material/Language';
import ImageIcon from '@mui/icons-material/Image';
import DescriptionIcon from '@mui/icons-material/Description';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ScienceIcon from '@mui/icons-material/Science';
import {useQuery} from '@apollo/client';
import {Virtuoso} from 'react-virtuoso';
import {GET_SOURCE_ITEMS_CONNECTION} from '../../graphql/queries';
import {colors} from '../../theme/colors';
import type {SourceItemFilterState} from './SourceItemFilterBar';
import type {GroupBy} from '../../pages/SourceLibraryPage';

function formatRelativeTime(dateStr: string): string {
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return d.toLocaleDateString('en-US', {month: 'short', day: 'numeric'});
}

function formatFileSize(bytes?: number): string {
  if (!bytes) return '\u2014';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function sourceTypeIcon(type: string) {
  switch (type) {
    case 'pdf': return <PictureAsPdfIcon sx={{fontSize: 20, color: colors.red[600]}} />;
    case 'url':
    case 'html': return <LanguageIcon sx={{fontSize: 20, color: colors.blue[500]}} />;
    case 'image': return <ImageIcon sx={{fontSize: 20, color: colors.purple[500]}} />;
    default: return <DescriptionIcon sx={{fontSize: 20, color: colors.gray[500]}} />;
  }
}

function extractionStatusChip(status: string) {
  switch (status) {
    case 'COMPLETED':
      return <Chip label="Completed" size="small" color="success" variant="outlined" sx={{height: 20, fontSize: 11}} />;
    case 'PROCESSING':
      return <Chip label="Processing" size="small" color="info" variant="outlined" sx={{height: 20, fontSize: 11}} />;
    case 'FAILED':
      return <Chip label="Failed" size="small" color="error" variant="outlined" sx={{height: 20, fontSize: 11}} />;
    default:
      return <Chip label="Pending" size="small" variant="outlined" sx={{height: 20, fontSize: 11}} />;
  }
}

interface SourceItemListProps {
  orgId: string;
  filter: SourceItemFilterState;
  selectedIds: Set<string>;
  onToggleSelect: (id: string) => void;
  onItemClick: (item: any) => void;
  onDeleteClick: (ids: string[]) => void;
  groupBy: GroupBy;
  evalCaseIds?: Set<string>;
  onTotalCountChange?: (count: number) => void;
}

const PAGE_SIZE = 50;

// Memoized row component
const SourceItemRow = React.memo(function SourceItemRow({
  item,
  selected,
  onToggle,
  onSelect,
  onDelete,
  isEvalCase,
}: {
  item: any;
  selected: boolean;
  onToggle: (id: string) => void;
  onSelect: (id: string) => void;
  onDelete: (ids: string[]) => void;
  isEvalCase?: boolean;
}) {
  return (
    <Box
      onClick={() => onSelect(item.id)}
      sx={{
        px: 2,
        py: 1.5,
        display: 'flex',
        alignItems: 'center',
        cursor: 'pointer',
        borderBottom: '1px solid #F3F4F6',
        '&:hover': {bgcolor: 'action.hover'},
        '&:hover .delete-btn': {opacity: 1},
      }}
    >
      <Box sx={{width: 40, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
        <Checkbox
          size="small"
          checked={selected}
          onChange={() => onToggle(item.id)}
          onClick={(e) => e.stopPropagation()}
          sx={{p: 0}}
        />
      </Box>
      <Box sx={{flex: 1, display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0}}>
        {sourceTypeIcon(item.type)}
        <Typography sx={{fontSize: 14, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
          {item.title}
        </Typography>
        {isEvalCase && (
          <Tooltip title="Eval test case" placement="top">
            <ScienceIcon sx={{fontSize: 14, color: '#8B5CF6', flexShrink: 0}} />
          </Tooltip>
        )}
      </Box>
      <Typography sx={{width: 80, fontSize: 13, color: '#6B7280', textTransform: 'uppercase'}}>
        {item.type}
      </Typography>
      <Typography sx={{width: 80, fontSize: 13, color: '#6B7280'}}>
        {formatFileSize(item.sizeBytes)}
      </Typography>
      <Box sx={{width: 80}}>
        {(() => {
          const status = item.analysisStatus || item.summarizationStatus;
          if (status === 'completed') return <Chip label="Done" size="small" sx={{height: 20, fontSize: 11, bgcolor: '#D1FAE5', color: '#065F46'}} />;
          if (status === 'processing') return <Chip label="..." size="small" color="info" variant="outlined" sx={{height: 20, fontSize: 11}} />;
          if (status === 'failed') return <Chip label="Failed" size="small" color="error" variant="outlined" sx={{height: 20, fontSize: 11}} />;
          return <Typography sx={{fontSize: 13, color: '#D1D5DB'}}>{'\u2014'}</Typography>;
        })()}
      </Box>
      <Box sx={{width: 90}}>
        {(() => {
          const isProcess = item.classificationOverride?.isProcess ?? item.analysis?.guideable ?? item.classificationWave1?.is_process;
          const status = item.analysisStatus || item.classificationStatus;
          if (isProcess === true) return <Chip label="Process" size="small" sx={{height: 20, fontSize: 11, bgcolor: '#D1FAE5', color: '#065F46'}} />;
          if (isProcess === false || status === 'completed') return <Chip label="Other" size="small" variant="outlined" sx={{height: 20, fontSize: 11, color: '#6B7280'}} />;
          if (status === 'processing') return <Chip label="..." size="small" color="warning" variant="outlined" sx={{height: 20, fontSize: 11}} />;
          return <Typography sx={{fontSize: 13, color: '#D1D5DB'}}>{'\u2014'}</Typography>;
        })()}
      </Box>
      <Box sx={{width: 70}}>
        {(() => {
          const topic = item.analysis?.topic;
          if (topic) return <Chip label={topic} size="small" variant="outlined" sx={{height: 20, fontSize: 11, maxWidth: 70, '& .MuiChip-label': {overflow: 'hidden', textOverflow: 'ellipsis'}}} />;
          const refsProcess = item.classificationWave1?.references_process;
          if (refsProcess) return <Chip label="Yes" size="small" sx={{height: 20, fontSize: 11, bgcolor: '#FEF3C7', color: '#92400E'}} />;
          return <Typography sx={{fontSize: 13, color: '#D1D5DB'}}>{'\u2014'}</Typography>;
        })()}
      </Box>
      <Box sx={{width: 80}}>
        {extractionStatusChip(item.extractionStatus)}
      </Box>
      <Typography sx={{width: 100, fontSize: 13, color: '#9CA3AF'}}>
        {formatRelativeTime(item.createdAt)}
      </Typography>
      <Box className="delete-btn" sx={{width: 40, display: 'flex', justifyContent: 'center', opacity: 0, transition: 'opacity 0.15s'}}>
        <IconButton
          size="small"
          onClick={(e) => { e.stopPropagation(); onDelete([item.id]); }}
          sx={{color: '#9CA3AF', '&:hover': {color: colors.red[600]}}}
        >
          <DeleteOutlineIcon sx={{fontSize: 18}} />
        </IconButton>
      </Box>
    </Box>
  );
});

// Group header row for source_page grouping
function GroupHeaderRow({groupKey, title}: {groupKey: string; title?: string}) {
  return (
    <Box
      sx={{
        px: 2,
        py: 1,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        bgcolor: '#FAFAFA',
        borderBottom: '1px solid #E5E7EB',
        borderTop: '1px solid #E5E7EB',
      }}
    >
      <LanguageIcon sx={{fontSize: 16, color: '#6B7280'}} />
      <Box sx={{minWidth: 0, flex: 1}}>
        <Typography sx={{fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
          {title || groupKey}
        </Typography>
        {title && (
          <Typography sx={{fontSize: 11, color: '#9CA3AF', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
            {groupKey}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

type FlatRow = { type: 'header'; groupKey: string; title?: string } | { type: 'item'; item: any };

function buildGroupedRows(items: any[]): FlatRow[] {
  const groups = new Map<string, { title?: string; items: any[] }>();
  const otherItems: any[] = [];

  for (const item of items) {
    const firstPage = item.linkedFromPages?.[0];
    if (firstPage?.url) {
      const key = firstPage.url;
      if (!groups.has(key)) {
        groups.set(key, { title: firstPage.title, items: [] });
      }
      groups.get(key)!.items.push(item);
    } else {
      otherItems.push(item);
    }
  }

  const rows: FlatRow[] = [];

  // Sort groups by item count descending
  const sortedGroups = Array.from(groups.entries()).sort((a, b) => b[1].items.length - a[1].items.length);

  for (const [groupKey, group] of sortedGroups) {
    rows.push({ type: 'header', groupKey, title: group.title });
    for (const item of group.items) {
      rows.push({ type: 'item', item });
    }
  }

  if (otherItems.length > 0) {
    rows.push({ type: 'header', groupKey: 'Other / Direct uploads' });
    for (const item of otherItems) {
      rows.push({ type: 'item', item });
    }
  }

  return rows;
}

export default function SourceItemList({
  orgId,
  filter,
  selectedIds,
  onToggleSelect,
  onItemClick,
  onDeleteClick,
  groupBy,
  evalCaseIds,
  onTotalCountChange,
}: SourceItemListProps) {
  // Build the filter variable, stripping undefined/empty values
  const filterVar = useMemo(() => {
    const f: Record<string, any> = {};
    if (filter.nodeId) f.nodeId = filter.nodeId;
    if (filter.q) f.q = filter.q;
    if (filter.types?.length) f.types = filter.types;
    if (filter.extractionStatuses?.length) f.extractionStatuses = filter.extractionStatuses;
    if (filter.summarizationStatuses?.length) f.summarizationStatuses = filter.summarizationStatuses;
    if (filter.analysisStatuses?.length) f.analysisStatuses = filter.analysisStatuses;
    if (filter.topic) f.topic = filter.topic;
    if (filter.audience) f.audience = filter.audience;
    if (filter.isProcess !== undefined && filter.isProcess !== null) f.isProcess = filter.isProcess;
    if (filter.referencesProcess !== undefined && filter.referencesProcess !== null) f.referencesProcess = filter.referencesProcess;
    if (filter.unclassified) f.unclassified = filter.unclassified;
    return Object.keys(f).length > 0 ? f : undefined;
  }, [filter]);

  const {data, loading, fetchMore} = useQuery(GET_SOURCE_ITEMS_CONNECTION, {
    variables: {orgId, filter: filterVar, first: PAGE_SIZE},
    pollInterval: 5000,
  });

  const edges = data?.sourceItemsConnection?.edges ?? [];
  const pageInfo = data?.sourceItemsConnection?.pageInfo;
  const totalCount = data?.sourceItemsConnection?.totalCount ?? 0;
  const items = useMemo(() => edges.map((e: any) => e.node), [edges]);

  // Expose total count to parent
  React.useEffect(() => {
    onTotalCountChange?.(totalCount);
  }, [totalCount, onTotalCountChange]);

  const groupedRows = useMemo<FlatRow[]>(() => {
    if (groupBy !== 'source_page') return [];
    return buildGroupedRows(items);
  }, [items, groupBy]);

  const handleEndReached = useCallback(() => {
    if (!pageInfo?.hasNextPage || !pageInfo?.endCursor) return;
    fetchMore({
      variables: {after: pageInfo.endCursor},
    });
  }, [fetchMore, pageInfo]);

  const handleToggle = useCallback((id: string) => {
    onToggleSelect(id);
  }, [onToggleSelect]);

  const handleSelect = useCallback((id: string) => {
    const item = items.find((i: any) => i.id === id);
    if (item) onItemClick(item);
  }, [items, onItemClick]);

  if (loading && items.length === 0) {
    return (
      <Box sx={{py: 3}}>
        {Array.from({length: 8}).map((_, i) => (
          <Skeleton key={i} variant="rectangular" height={44} sx={{mb: 0.5, borderRadius: 1}} />
        ))}
      </Box>
    );
  }

  if (!loading && items.length === 0) {
    return (
      <Box sx={{py: 6, textAlign: 'center'}}>
        <Typography sx={{fontSize: 16, fontWeight: 600, color: '#6B7280'}}>
          No documents found
        </Typography>
        <Typography sx={{fontSize: 14, color: '#9CA3AF', mt: 0.5}}>
          {filter.nodeId || filter.q || filter.types?.length || filter.extractionStatuses?.length
            ? 'Try adjusting your filters'
            : 'Drop files above to get started'}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{flex: 1, display: 'flex', flexDirection: 'column'}}>
      {/* Column headers */}
      <Box
        sx={{
          px: 2,
          py: 0.75,
          display: 'flex',
          alignItems: 'center',
          borderBottom: '1px solid #E5E7EB',
          bgcolor: '#FAFAFA',
        }}
      >
        <Box sx={{width: 40}} />
        {[
          {label: 'Name', flex: 1, width: undefined},
          {label: 'Type', flex: undefined, width: 80},
          {label: 'Size', flex: undefined, width: 80},
          {label: 'Analysis', flex: undefined, width: 80},
          {label: 'Class.', flex: undefined, width: 90},
          {label: 'Topic', flex: undefined, width: 70},
          {label: 'Extraction', flex: undefined, width: 80},
          {label: 'Added', flex: undefined, width: 100},
          {label: '\u200B', flex: undefined, width: 40},
        ].map(col => (
          <Typography
            key={col.label}
            sx={{
              flex: col.flex,
              width: col.width,
              fontSize: 11,
              fontWeight: 600,
              color: '#9CA3AF',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            {col.label}
          </Typography>
        ))}
      </Box>
      <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 0.5, borderBottom: '1px solid #F3F4F6'}}>
        <Typography sx={{fontSize: 12, color: '#9CA3AF'}}>
          {totalCount} document{totalCount === 1 ? '' : 's'}
          {items.length < totalCount ? ` (showing ${items.length})` : ''}
        </Typography>
        {loading && <CircularProgress size={12} />}
      </Box>
      {/* Virtualized list */}
      <Box sx={{flex: 1}}>
        {groupBy === 'source_page' ? (
          <Virtuoso
            style={{height: '100%'}}
            data={groupedRows}
            endReached={handleEndReached}
            overscan={200}
            itemContent={(_index, row: FlatRow) => {
              if (row.type === 'header') {
                return <GroupHeaderRow groupKey={row.groupKey} title={row.title} />;
              }
              return (
                <SourceItemRow
                  item={row.item}
                  selected={selectedIds.has(row.item.id)}
                  onToggle={handleToggle}
                  onSelect={handleSelect}
                  onDelete={onDeleteClick}
                  isEvalCase={evalCaseIds?.has(row.item.id)}
                />
              );
            }}
          />
        ) : (
          <Virtuoso
            style={{height: '100%'}}
            data={items}
            endReached={handleEndReached}
            overscan={200}
            itemContent={(_index, item: any) => (
              <SourceItemRow
                item={item}
                selected={selectedIds.has(item.id)}
                onToggle={handleToggle}
                onSelect={handleSelect}
                onDelete={onDeleteClick}
                isEvalCase={evalCaseIds?.has(item.id)}
              />
            )}
          />
        )}
      </Box>
    </Box>
  );
}
